import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Platform,
  Linking,
  RefreshControl,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import PantallaSegura from '../components/PantallaSegura';
import ScreenBackground from '../components/ScreenBackground';
import { useThemeColors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import {
  callEscaparateDeLogistica,
  callCotizarServicioLogistica,
  callIniciarServicioLogistica,
  callCompletarServicioLogistica,
  callContactoDelServicioLogistica,
  callCancelarServicioLogistica,
} from '../services/cloudFunctions';
import { mensajeDeError } from '../utils/mensajeDeError';
import { VERTICAL_LOGISTICA_VISIBLE } from '../constants/featureFlags';
import { desgloseDeComision } from '../constants/comisionLogistica';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TRABAJOS — el lado del transportista
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Dos partes: lo que hay para cotizar, y lo que ya lleva.
 *
 * ── LO QUE VE Y LO QUE NO ────────────────────────────────────────────────
 *
 * El escaparate viene de una callable que PROYECTA campos: el sector, no la
 * calle, y una coordenada redondeada a un kilómetro, no el punto. La dirección
 * completa aparece cuando el cliente acepta SU cotización — y entonces sale de
 * `listenToServiciosDeLogistica`, que lee el documento de verdad porque ya es
 * suyo. No es una decisión de pantalla: es que la regla de Firestore no le deja
 * ver los trabajos de nadie más.
 *
 * ── AL COTIZAR SE LE DICE LO QUE LE QUEDA, ANTES DE CONFIRMAR ───────────
 *
 * No «10% de comisión»: el peso exacto que se queda él. La tabla es marginal y
 * nadie debería tener que calcularla. Y el número sale de
 * `constants/comisionLogistica.ts`, que es espejo exacto del que cobra el
 * servidor — si se separan, la app promete una cosa y se cobra otra, y eso ya
 * pasó dos veces en este proyecto.
 */

function pesos(n: unknown): string {
  const v = Number(n ?? 0) || 0;
  return `RD$${v.toLocaleString('es-DO')}`;
}

const ROTULO: Record<string, { texto: string; color: string }> = {
  ACEPTADO: { texto: 'Aceptado — por recoger', color: '#2563EB' },
  EN_CURSO: { texto: 'En curso', color: '#10B981' },
  ENTREGADO: { texto: 'Entregado', color: '#6B7280' },
  CANCELADO: { texto: 'Cancelado', color: '#6B7280' },
};

export default function LogisticaTrabajosScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const uid = useAppStore((s) => s.userFirebaseUid);

  const [pestana, setPestana] = React.useState<'BUSCAR' | 'MIOS'>('BUSCAR');
  const [cargandoEscaparate, setCargandoEscaparate] = React.useState(true);
  const [escaparate, setEscaparate] = React.useState<any | null>(null);
  const [mios, setMios] = React.useState<any[]>([]);
  const [montos, setMontos] = React.useState<Record<string, string>>({});
  const [ocupado, setOcupado] = React.useState<string | null>(null);
  const [pinEscrito, setPinEscrito] = React.useState<Record<string, string>>({});

  const avisar = (titulo: string, texto: string) => {
    if (Platform.OS === 'web') window.alert(`${titulo}\n\n${texto}`);
    else Alert.alert(titulo, texto);
  };

  const traerEscaparate = React.useCallback(async () => {
    setCargandoEscaparate(true);
    try {
      const r = await callEscaparateDeLogistica({});
      setEscaparate(r?.data ?? null);
    } catch (e: any) {
      // El motivo se ENSEÑA. Un escaparate vacío sin explicación se lee como
      // «no hay trabajos», cuando lo que pasa es que su alta está en revisión.
      // `fallo: true` distingue «no se pudo cargar» de «no hay trabajos». Sin
      // esa marca la pantalla enseñaba las DOS cosas a la vez — «no pudimos
      // cargar» arriba y «no hay trabajos que encajen» debajo»— que es peor
      // que no decir nada: el transportista no sabe si esperar o si la app
      // está rota.
      setEscaparate({
        trabajos: [],
        puedeCotizar: false,
        hayMas: false,
        fallo: true,
        motivo: mensajeDeError(e, 'No pudimos cargar los trabajos. Revisa tu conexión.'),
      });
    } finally {
      setCargandoEscaparate(false);
    }
  }, []);

  React.useEffect(() => {
    traerEscaparate();
  }, [traerEscaparate]);

  React.useEffect(() => {
    if (!uid) return;
    let vivo = true;
    const parar = firestoreService.listenToServiciosDeLogistica(uid, 'PROVEEDOR', (lista) => {
      if (vivo) setMios(lista);
    });
    return () => {
      vivo = false;
      try {
        if (typeof parar === 'function') parar();
      } catch {
        /* desmontaje */
      }
    };
  }, [uid]);

  const cotizar = async (servicioId: string) => {
    const bruto = (montos[servicioId] ?? '').replace(/[.,\s]/g, '');
    if (!/^\d{2,7}$/.test(bruto)) {
      avisar('Revisa el precio', 'Escribe cuánto cobras, solo en números. Ejemplo: 8000.');
      return;
    }
    const monto = Number(bruto);
    const d = desgloseDeComision(monto);

    const texto =
      `Cobras ${pesos(d.monto)}.\n\n` +
      `Comisión de Go Safely: ${pesos(d.comision)}\n` +
      `Tú recibes: ${pesos(d.recibe)}\n\n` +
      'El cliente te paga a ti, directo. La comisión se te acumula y se cobra aparte.';

    const hacer = async () => {
      setOcupado(servicioId);
      try {
        await callCotizarServicioLogistica({ servicioId, monto });
        avisar('Cotización enviada', 'El cliente la va a ver. Si te elige, te avisamos.');
        await traerEscaparate();
      } catch (e: any) {
        avisar('No pudimos enviarla', mensajeDeError(e, 'Inténtalo otra vez.'));
      } finally {
        setOcupado(null);
      }
    };

    if (Platform.OS === 'web') {
      if (window.confirm(texto)) await hacer();
      return;
    }
    Alert.alert('¿Mandar esta cotización?', texto, [
      { text: 'No', style: 'cancel' },
      { text: 'Sí, mandarla', onPress: hacer },
    ]);
  };

  const iniciar = async (servicioId: string) => {
    setOcupado(servicioId);
    try {
      await callIniciarServicioLogistica({ servicioId });
    } catch (e: any) {
      avisar('No pudimos iniciarlo', mensajeDeError(e, 'Inténtalo otra vez.'));
    } finally {
      setOcupado(null);
    }
  };

  const completar = async (servicioId: string) => {
    const pin = (pinEscrito[servicioId] ?? '').replace(/\D/g, '');
    if (pin.length !== 4) {
      avisar('El PIN', 'Pídele al cliente su PIN de entrega: son cuatro números.');
      return;
    }
    setOcupado(servicioId);
    try {
      const r = await callCompletarServicioLogistica({ servicioId, pin });
      const d = r?.data;
      avisar(
        'Entrega cerrada',
        `Cobras ${pesos(d?.recibe)} al cliente.\n\n` +
          `Comisión de este trabajo: ${pesos(d?.comision)}\n` +
          `Tu comisión acumulada: ${pesos(d?.comisionPendiente)}`
      );
      setPinEscrito((p) => ({ ...p, [servicioId]: '' }));
    } catch (e: any) {
      avisar('El PIN no cuadra', mensajeDeError(e, 'Pídeselo otra vez al cliente.'));
    } finally {
      setOcupado(null);
    }
  };

  /**
   * ══ CANCELAR, QUE NO EXISTÍA EN ESTA PANTALLA ════════════════════════
   *
   * Un trabajo ACEPTADO o EN_CURSO no lo podía cancelar NINGUNA de las dos
   * partes: aquí solo había «Voy en camino» y la casilla del PIN. Si al
   * transportista se le rompía el camión, el trabajo se quedaba ahí para
   * siempre — y el cliente tampoco podía reabrirlo para que lo cotizaran
   * otros. Lo cazó la revisión adversarial.
   *
   * Cancelar NO devenga comisión: el servidor no cobra por un trabajo que no se
   * hizo. Pero sí se avisa de que cancelar seguido tiene consecuencias, porque
   * al otro lado hay alguien esperando.
   */
  const cancelar = async (servicioId: string) => {
    const texto =
      'El cliente va a recibir el aviso de que no vas. Cancelar no te cuesta comisión, ' +
      'pero hay alguien esperando su carga.';
    const sigue =
      Platform.OS === 'web'
        ? window.confirm(`¿Cancelar este trabajo?\n\n${texto}`)
        : await new Promise<boolean>((resolver) =>
            Alert.alert('¿Cancelar este trabajo?', texto, [
              { text: 'No', style: 'cancel', onPress: () => resolver(false) },
              { text: 'Sí, cancelar', style: 'destructive', onPress: () => resolver(true) },
            ])
          );
    if (!sigue) return;
    setOcupado(servicioId);
    try {
      await callCancelarServicioLogistica({ servicioId, motivo: 'El transportista lo canceló' });
    } catch (e: any) {
      avisar('No pudimos cancelarlo', mensajeDeError(e, 'Inténtalo otra vez.'));
    } finally {
      setOcupado(null);
    }
  };

  const llamar = async (servicioId: string) => {
    try {
      const r = await callContactoDelServicioLogistica({ servicioId });
      const tel = String(r?.data?.telefono ?? '').replace(/\D/g, '');
      if (!tel) {
        avisar('Sin teléfono', 'Este cliente no tiene un número guardado.');
        return;
      }
      const url = `tel:${tel}`;
      if (await Linking.canOpenURL(url)) await Linking.openURL(url);
      else avisar(String(r?.data?.nombre ?? 'Cliente'), tel);
    } catch (e: any) {
      avisar('No pudimos darte el contacto', mensajeDeError(e, 'Inténtalo otra vez.'));
    }
  };

  if (!VERTICAL_LOGISTICA_VISIBLE) return null;

  const activos = mios.filter((s) => ['ACEPTADO', 'EN_CURSO'].includes(String(s.estado)));
  const cerrados = mios.filter((s) => ['ENTREGADO', 'CANCELADO'].includes(String(s.estado)));

  return (
    <ScreenBackground>
      <PantallaSegura>
        <View style={[estilos.cabecera, { borderBottomColor: colors.border }]}>
          <TouchableOpacity onPress={() => router.back()} style={estilos.salida}>
            <Ionicons name="chevron-back" size={22} color={colors.text} />
            <Text style={[estilos.salidaTexto, { color: colors.text }]}>Volver</Text>
          </TouchableOpacity>
        </View>

        <View style={[estilos.pestanas, { borderColor: colors.border }]}>
          {(['BUSCAR', 'MIOS'] as const).map((p) => (
            <TouchableOpacity
              key={p}
              onPress={() => setPestana(p)}
              style={[estilos.pestana, pestana === p && { backgroundColor: '#EF6C00' }]}
            >
              <Text
                style={[
                  estilos.pestanaTexto,
                  { color: pestana === p ? '#FFF' : colors.textSecondary },
                ]}
              >
                {p === 'BUSCAR' ? 'Buscar trabajo' : `Mis trabajos${activos.length ? ` (${activos.length})` : ''}`}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        <ScrollView
          contentContainerStyle={estilos.cuerpo}
          refreshControl={
            <RefreshControl refreshing={cargandoEscaparate} onRefresh={traerEscaparate} />
          }
        >
          {pestana === 'BUSCAR' && (
            <>
              {escaparate?.puedeCotizar === false && (
                <View style={[estilos.bloque, { backgroundColor: colors.card, borderColor: '#D97706' }]}>
                  <Text style={[estilos.bloqueTitulo, { color: colors.text }]}>
                    {escaparate?.fallo ? 'No pudimos cargar los trabajos' : 'No puedes cotizar ahora mismo'}
                  </Text>
                  <Text style={[estilos.bloqueTexto, { color: colors.textSecondary }]}>
                    {escaparate?.motivo ??
                      `Tienes ${pesos(escaparate?.deudaPendiente)} de comisión pendiente. Págala y vuelves a cotizar al instante. Los trabajos que ya tienes en curso no se tocan.`}
                  </Text>
                  <TouchableOpacity
                    onPress={escaparate?.fallo ? traerEscaparate : () => router.push('/logistica' as any)}
                    style={[estilos.boton, { backgroundColor: '#EF6C00' }]}
                  >
                    <Text style={estilos.botonTexto}>
                      {escaparate?.fallo ? 'Volver a intentar' : 'Ver mi comisión'}
                    </Text>
                  </TouchableOpacity>
                </View>
              )}

              {cargandoEscaparate && !escaparate && (
                <View style={estilos.centro}>
                  <ActivityIndicator color={colors.primary} />
                </View>
              )}

              {escaparate && !escaparate.fallo && (escaparate.trabajos ?? []).length === 0 && !cargandoEscaparate && (
                <View style={[estilos.bloque, { backgroundColor: colors.card, borderColor: colors.border }]}>
                  <Text style={[estilos.bloqueTexto, { color: colors.textSecondary }]}>
                    Ahora mismo no hay trabajos que encajen con tu vehículo. Desliza hacia abajo
                    para volver a mirar.
                  </Text>
                </View>
              )}

              {(escaparate?.trabajos ?? []).map((t: any) => {
                const escrito = (montos[t.servicioId] ?? '').replace(/[.,\s]/g, '');
                const previo = /^\d{2,7}$/.test(escrito) ? desgloseDeComision(Number(escrito)) : null;
                return (
                  <View
                    key={t.servicioId}
                    style={[estilos.bloque, { backgroundColor: colors.card, borderColor: colors.border }]}
                  >
                    <View style={estilos.filaRotulo}>
                      <Text style={[estilos.tipo, { color: colors.text }]}>{String(t.tipo ?? '')}</Text>
                      {t.montoOfrecido ? (
                        <Text style={[estilos.ofrece, { color: '#10B981' }]}>
                          Ofrece {pesos(t.montoOfrecido)}
                        </Text>
                      ) : null}
                    </View>
                    <Text style={[estilos.bloqueTexto, { color: colors.textSecondary }]}>
                      {String(t.descripcion ?? '')}
                    </Text>
                    <Text style={[estilos.ruta, { color: colors.textSecondary }]}>
                      {t.sectorOrigen ?? 'Sector por confirmar'}  →  {t.sectorDestino ?? 'Sector por confirmar'}
                      {t.distanciaKm != null ? `  ·  ~${t.distanciaKm} km` : ''}
                    </Text>
                    {/* Se dice QUÉ se ve y qué no. Un proveedor que espera la
                        dirección y no la encuentra cree que la app está rota. */}
                    <Text style={[estilos.nota, { color: colors.textSecondary }]}>
                      La dirección exacta te la damos si el cliente te elige.
                    </Text>

                    {t.yaCotice ? (
                      <Text style={[estilos.yaCotice, { color: '#2563EB' }]}>
                        Ya cotizaste este trabajo. Si vuelves a mandar un precio, reemplaza al
                        anterior.
                      </Text>
                    ) : null}

                    {escaparate?.puedeCotizar !== false && (
                      <>
                        <View style={estilos.filaCotizar}>
                          <TextInput
                            value={montos[t.servicioId] ?? ''}
                            onChangeText={(v) => setMontos((p) => ({ ...p, [t.servicioId]: v }))}
                            placeholder="Tu precio"
                            placeholderTextColor={colors.textSecondary}
                            keyboardType="number-pad"
                            maxLength={9}
                            style={[
                              estilos.campo,
                              { backgroundColor: colors.background, borderColor: colors.border, color: colors.text },
                            ]}
                          />
                          <TouchableOpacity
                            onPress={() => cotizar(t.servicioId)}
                            disabled={ocupado === t.servicioId}
                            style={[estilos.cotizar, { backgroundColor: '#EF6C00' }]}
                          >
                            {ocupado === t.servicioId ? (
                              <ActivityIndicator color="#FFF" size="small" />
                            ) : (
                              <Text style={estilos.cotizarTexto}>Cotizar</Text>
                            )}
                          </TouchableOpacity>
                        </View>
                        {previo && (
                          <Text style={[estilos.previo, { color: colors.text }]}>
                            Comisión {pesos(previo.comision)} · tú recibes {pesos(previo.recibe)}
                          </Text>
                        )}
                      </>
                    )}
                  </View>
                );
              })}

              {escaparate?.hayMas && (
                <Text style={[estilos.nota, { color: colors.textSecondary, textAlign: 'center' }]}>
                  Hay más trabajos de los que caben aquí. Cotiza o descarta estos y vuelve a
                  mirar.
                </Text>
              )}
            </>
          )}

          {pestana === 'MIOS' && (
            <>
              {activos.length === 0 && cerrados.length === 0 && (
                <View style={[estilos.bloque, { backgroundColor: colors.card, borderColor: colors.border }]}>
                  <Text style={[estilos.bloqueTexto, { color: colors.textSecondary }]}>
                    Todavía no te han elegido para ningún trabajo.
                  </Text>
                </View>
              )}

              {[...activos, ...cerrados].map((s) => {
                const rot = ROTULO[String(s.estado)] ?? { texto: String(s.estado), color: colors.textSecondary };
                const d = desgloseDeComision(Number(s.montoAcordado ?? 0));
                return (
                  <View
                    key={s.id}
                    style={[estilos.bloque, { backgroundColor: colors.card, borderColor: colors.border }]}
                  >
                    <View style={estilos.filaRotulo}>
                      <Text style={[estilos.tipo, { color: colors.text }]}>{String(s.tipo ?? '')}</Text>
                      <Text style={[estilos.estado, { color: rot.color }]}>{rot.texto}</Text>
                    </View>
                    <Text style={[estilos.bloqueTexto, { color: colors.textSecondary }]}>
                      {String(s.descripcion ?? '')}
                    </Text>
                    {/* Aquí SÍ sale la dirección completa: ya es su trabajo. */}
                    <Text style={[estilos.ruta, { color: colors.text }]}>
                      Recoge en: {String(s.origen?.texto ?? '—')}
                    </Text>
                    <Text style={[estilos.ruta, { color: colors.text }]}>
                      Entrega en: {String(s.destino?.texto ?? '—')}
                    </Text>
                    <Text style={[estilos.cobro, { color: colors.text }]}>
                      Cobras {pesos(s.montoAcordado)} · comisión {pesos(s.comision ?? d.comision)} · te
                      quedan {pesos(Number(s.montoAcordado ?? 0) - Number(s.comision ?? d.comision))}
                    </Text>

                    {['ACEPTADO', 'EN_CURSO'].includes(String(s.estado)) && (
                      <TouchableOpacity
                        onPress={() => llamar(s.id)}
                        style={[estilos.botonSuave, { borderColor: colors.border, marginTop: 10 }]}
                      >
                        <Ionicons name="call-outline" size={16} color={colors.text} />
                        <Text style={[estilos.botonSuaveTexto, { color: colors.text }]}>
                          Llamar al cliente
                        </Text>
                      </TouchableOpacity>
                    )}

                    {['ACEPTADO', 'EN_CURSO'].includes(String(s.estado)) && (
                      <TouchableOpacity
                        onPress={() => cancelar(s.id)}
                        disabled={ocupado === s.id}
                        style={[estilos.botonSuave, { borderColor: colors.border, marginTop: 10 }]}
                      >
                        <Text style={[estilos.botonSuaveTexto, { color: '#DC2626' }]}>
                          No puedo hacerlo — cancelar
                        </Text>
                      </TouchableOpacity>
                    )}

                    {String(s.estado) === 'ACEPTADO' && (
                      <TouchableOpacity
                        onPress={() => iniciar(s.id)}
                        disabled={ocupado === s.id}
                        style={[estilos.boton, { backgroundColor: '#2563EB' }]}
                      >
                        {ocupado === s.id ? (
                          <ActivityIndicator color="#FFF" />
                        ) : (
                          <Text style={estilos.botonTexto}>Voy en camino</Text>
                        )}
                      </TouchableOpacity>
                    )}

                    {String(s.estado) === 'EN_CURSO' && (
                      <View style={[estilos.pinCaja, { borderColor: '#10B981' }]}>
                        <Text style={[estilos.pinRotulo, { color: colors.textSecondary }]}>
                          PÍDELE EL PIN AL CLIENTE
                        </Text>
                        <Text style={[estilos.pinAyuda, { color: colors.textSecondary }]}>
                          Él lo tiene en su app. Sin ese PIN el trabajo no se cierra — y es lo
                          que le da la seguridad de que entregaste.
                        </Text>
                        <View style={estilos.filaCotizar}>
                          <TextInput
                            value={pinEscrito[s.id] ?? ''}
                            onChangeText={(v) => setPinEscrito((p) => ({ ...p, [s.id]: v }))}
                            placeholder="0000"
                            placeholderTextColor={colors.textSecondary}
                            keyboardType="number-pad"
                            maxLength={4}
                            style={[
                              estilos.campo,
                              estilos.campoPin,
                              { backgroundColor: colors.background, borderColor: colors.border, color: colors.text },
                            ]}
                          />
                          <TouchableOpacity
                            onPress={() => completar(s.id)}
                            disabled={ocupado === s.id}
                            style={[estilos.cotizar, { backgroundColor: '#10B981' }]}
                          >
                            {ocupado === s.id ? (
                              <ActivityIndicator color="#FFF" size="small" />
                            ) : (
                              <Text style={estilos.cotizarTexto}>Entregado</Text>
                            )}
                          </TouchableOpacity>
                        </View>
                      </View>
                    )}
                  </View>
                );
              })}
            </>
          )}
          <View style={{ height: 40 }} />
        </ScrollView>
      </PantallaSegura>
    </ScreenBackground>
  );
}

const estilos = StyleSheet.create({
  cabecera: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1 },
  salida: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingHorizontal: 4 },
  salidaTexto: { fontSize: 16, marginLeft: 2 },
  pestanas: { flexDirection: 'row', margin: 16, borderRadius: 12, borderWidth: 1, overflow: 'hidden' },
  pestana: { flex: 1, paddingVertical: 12, alignItems: 'center' },
  pestanaTexto: { fontSize: 14, fontWeight: '700' },
  cuerpo: { paddingHorizontal: 16 },
  centro: { paddingVertical: 40, alignItems: 'center' },
  bloque: { borderRadius: 14, borderWidth: 1, padding: 16, marginBottom: 14 },
  bloqueTitulo: { fontSize: 16, fontWeight: '700', marginBottom: 8 },
  bloqueTexto: { fontSize: 14, lineHeight: 21 },
  filaRotulo: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  tipo: { fontSize: 16, fontWeight: '700', textTransform: 'capitalize' },
  estado: { fontSize: 12, fontWeight: '700' },
  ofrece: { fontSize: 13, fontWeight: '700' },
  ruta: { fontSize: 13, lineHeight: 19, marginTop: 6 },
  nota: { fontSize: 12, lineHeight: 18, marginTop: 8, fontStyle: 'italic' },
  yaCotice: { fontSize: 13, lineHeight: 19, marginTop: 8, fontWeight: '600' },
  cobro: { fontSize: 14, lineHeight: 21, marginTop: 10, fontWeight: '600' },
  filaCotizar: { flexDirection: 'row', gap: 10, marginTop: 10, alignItems: 'center' },
  campo: { flex: 1, borderRadius: 12, borderWidth: 1, paddingHorizontal: 14, paddingVertical: 12, fontSize: 15 },
  campoPin: { fontSize: 22, letterSpacing: 6, textAlign: 'center', fontWeight: '700' },
  cotizar: { borderRadius: 12, paddingVertical: 13, paddingHorizontal: 18 },
  cotizarTexto: { color: '#FFF', fontSize: 15, fontWeight: '700' },
  previo: { fontSize: 13, lineHeight: 20, marginTop: 8, fontWeight: '600' },
  pinCaja: { borderRadius: 12, borderWidth: 2, padding: 14, marginTop: 12 },
  pinRotulo: { fontSize: 11, fontWeight: '700', letterSpacing: 1 },
  pinAyuda: { fontSize: 12, lineHeight: 18, marginTop: 6 },
  boton: { borderRadius: 14, paddingVertical: 14, alignItems: 'center', marginTop: 12 },
  botonTexto: { color: '#FFF', fontSize: 16, fontWeight: '700' },
  botonSuave: { flexDirection: 'row', gap: 8, borderRadius: 12, paddingVertical: 12, alignItems: 'center', justifyContent: 'center', borderWidth: 1 },
  botonSuaveTexto: { fontSize: 14, fontWeight: '600' },
});
